import { useEffect, useState } from 'react'
import { Permissions } from '../../../../types/user-types'
import useUser from '../../../hooks/use-user'
import { can, isDriller } from '../../../services/user-utils'
import YieldDrawDownSummaryTable from './summary-table'
import YieldCalculationsCard from './calculations-card'
import WellEditButtons from '../../wells/well-edit-buttons'
import { useWellState } from '../../../context/well-context'
import { HttpVerb } from '../../../../types/api-types'
import { useSaveWellData } from '../../../hooks/use-api'
import { HandleSave } from '../../../hooks/use-edit-form'
import useToastMessage from '../../../hooks/use-toast-message'
import WellViewButtons from '../../wells/well-view-buttons'
import { useWellId } from '../../../hooks/use-well-id'
import { useNavigate } from 'react-router-dom'
import { Button, Card, Heading, HeadingRow, RecordContent, RecordFooter, useTitleChange } from '@msaf/core-react'

export default function YieldDrawDownWrapper({ isViewMode }: { isViewMode?: boolean }) {
  const { user } = useUser()
  const [fields, setFields] = useState<Array<Record<string, string | number | boolean>> | null>(null)
  const [isRefreshCalculation, setIsRefreshCalculation] = useState(false)
  const wellId = useWellId()
  const navigate = useNavigate()

  const {
    wellState: { isDraft, hasNoYieldRecords },
  } = useWellState()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Yield drawdown tests`)
  const [isNoTestsChecked, setIsNoTestChecked] = useState(hasNoYieldRecords)
  const continueUrl = isDriller(user)
    ? `/wells/${wellId}/edit/additional-documents`
    : `/wells/${wellId}/edit/aquifer-tests`
  const addNewUrl = `/wells/${wellId}/edit/drilling-tests/new`
  const { mutate, isError, isSuccess, error } = useSaveWellData(`wells/${wellId}/drilling-tests/`, HttpVerb.PATCH)

  const {
    wellState: { isLegacyWell },
  } = useWellState()

  const handleNoTestsCheckbox = (checked: boolean) => {
    setIsNoTestChecked(checked)
  }

  useEffect(() => {
    setIsNoTestChecked(hasNoYieldRecords || isLegacyWell)
  }, [hasNoYieldRecords, isLegacyWell])

  const handleSave: HandleSave = (callback?) => {
    mutate &&
      mutate(JSON.stringify({ hasNoYieldRecords: isNoTestsChecked }), {
        onSuccess: (data) => {
          callback?.(data.id)
        },
      })
  }

  useToastMessage({ error, isError, isSuccess })

  useEffect(() => {
    setIsRefreshCalculation(true)
  }, [fields])

  return (
    <>
      <RecordContent>
        <Heading level={2}>Yield drawdown tests</Heading>
        <Card isFullWidth>
          <HeadingRow headingLevel={3} headingText='Summary'>
            {!isViewMode &&
              can(user, [Permissions.EDIT_WELL, Permissions.EDIT_WELL_UNSCOPED]) &&
              user?.objectPermissions?.canEdit && <Button label='Add new' onClick={() => navigate(addNewUrl)} />}
          </HeadingRow>
          <YieldDrawDownSummaryTable
            fields={fields}
            setFields={setFields}
            handleNoTestsCheckbox={handleNoTestsCheckbox}
            isNoTestsChecked={isNoTestsChecked}
            isViewMode={isViewMode}
          />
        </Card>
        {Array.isArray(fields) && !!fields.length && (
          <YieldCalculationsCard
            isRefreshCalculation={isRefreshCalculation}
            setIsRefreshCalculation={setIsRefreshCalculation}
          />
        )}
      </RecordContent>
      <RecordFooter>
        {!isViewMode ? (
          <WellEditButtons
            actions={
              !fields?.length
                ? [
                    {
                      label: 'Save and continue',
                      buttonStyle: 'primary',
                      onClick: () =>
                        handleSave(() => {
                          navigate(continueUrl)
                        }),
                    },
                    {
                      label: 'Save',
                      buttonStyle: 'primary',
                      onClick: () => handleSave(),
                    },
                    {
                      label: 'Cancel',
                      buttonStyle: 'text-action',
                      onClick: () => navigate(`/dashboard`),
                    },
                  ]
                : [
                    {
                      label: 'Continue',
                      buttonStyle: 'primary',
                      onClick: () => {
                        navigate(continueUrl)
                      },
                    },
                    {
                      label: 'Cancel',
                      buttonStyle: 'secondary',
                      onClick: () => navigate(`/dashboard`),
                    },
                  ]
            }
          />
        ) : (
          <WellViewButtons wellId={wellId} />
        )}
      </RecordFooter>
    </>
  )
}
